// M46 — Stale-bundle provider.
//
// Single source of truth for "is the loaded JS bundle older than the
// latest published changelog row." The shell's "What's new" nav item
// reads it to render a dot badge; the /changelog page reads it to
// render an inline "update available" card. Centralising the fetch +
// state here avoids two competing fetches and two diverging signals.
//
// Compares the bundle's baked-in `__APP_VERSION__` (frozen at build
// time by `define` in vite.config.ts) against the newest published row
// in `public.changelog_entries`. Fires the check on mount and on
// every `visibilitychange` to "visible" — that catches the common
// "tab parked for an hour, came back to it" case without polling.
//
// It now runs a SECOND, independent check in the same rhythm: the
// deployed build's own `version.json`. Two questions, two sources, and
// they are not interchangeable:
//
//   changelog row → "there are release notes you have not read"
//                   → the What's-new dot
//   version.json  → "the code under you has moved"
//                   → the update bar, and the warning that the next
//                     lazy route is about to 404 (see lazyRoute.ts)
//
// The changelog row is written by hand AFTER a deploy, so it cannot be
// the source for the second question — there is a window where the code
// has moved and the row does not exist yet, and that window is exactly
// when a user hits the stale-chunk error.
//
// A short interval joins the two triggers, because a tab that stays
// VISIBLE all afternoon never fires `visibilitychange` and would
// otherwise learn nothing until the user switched away and back.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getLatestChangelog } from '../lib/changelog';
import { isVersionNewer } from '../lib/version';
import { fetchDeployedVersion } from '../lib/deployedVersion';
import { StaleBundleContext, type StaleBundleValue } from './useStaleBundle';

/** Long enough to be invisible on the network tab, short enough that a tab
 *  left open through a deploy learns before the user clicks something. */
const POLL_MS = 5 * 60 * 1000;

export function StaleBundleProvider({ children }: { children: ReactNode }) {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [deployedVersion, setDeployedVersion] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      const latest = await getLatestChangelog();
      if (latest && isVersionNewer(latest.version, __APP_VERSION__)) {
        setLatestVersion(latest.version);
      }
    } catch {
      // A transient fetch failure (network blip, RLS denial when the
      // session lapsed, etc.) shouldn't pester the user. The next
      // mount or visibilitychange will retry.
    }
    // Separate try: a changelog failure must not skip the deploy check, which
    // is the one that matters for a tab about to break.
    const deployed = await fetchDeployedVersion();
    if (deployed && isVersionNewer(deployed, __APP_VERSION__)) {
      setDeployedVersion(deployed);
    }
  }, []);

  useEffect(() => {
    void check();
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    const timer = setInterval(() => {
      // Hidden tabs are already covered by `visibilitychange`, and browsers
      // throttle their timers anyway. Only poll what the user is looking at.
      if (document.visibilityState === 'visible') void check();
    }, POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(timer);
    };
  }, [check]);

  // Memo so consumers don't re-render on every parent render — only
  // when the actual state changes.
  const value = useMemo<StaleBundleValue>(
    () => ({ latestVersion, isStale: latestVersion !== null, deployedVersion }),
    [latestVersion, deployedVersion],
  );

  return (
    <StaleBundleContext.Provider value={value}>
      {children}
    </StaleBundleContext.Provider>
  );
}
